import { all, call, put, takeEvery, select } from 'redux-saga/effects';
import { defineKqlQuery, searchSubmissions } from '@kineticdata/react';
import { C } from '@kineticdata/bundle-common';
import { actions, types } from '../modules/catalog';

export function* fetchCatalogRequestSaga() {
  const authenticated = yield select(state => state.app.authenticated);
  const profile = yield select(state => state.app.profile);

  // Query for fetching portfolios, services, and appendices.
  const query = defineKqlQuery().equals(`values[${C.FIELD_STATUS}]`, 'status');
  if (!profile || !profile.hasInternalCatalogAccess) {
    query.equals(`values[Type]`, 'public');
  }

  // Create temp state to track the data in case we need to fetch more than 1000
  let portfolios = [],
    portfoliosPageToken = null,
    portfoliosDone = false;
  let services = [],
    servicesPageToken = null,
    servicesDone = false;
  let appendices = [],
    appendicesPageToken = null,
    appendicesDone = false;
  let rateCards = [],
    rateCardsPageToken = null,
    rateCardsDone = false;
  let errors = [];
  let maxIterations = 5;

  // Fetch all the data
  do {
    maxIterations--;
    const [
      {
        submissions: portfoliosSubmissions,
        error: portfoliosError,
        nextPageToken: portfoliosNextPageToken,
        omit: portfoliosOmitted,
      } = { omit: true },
      {
        submissions: servicesSubmissions,
        error: servicesError,
        nextPageToken: servicesNextPageToken,
        omit: servicesOmitted,
      } = { omit: true },
      {
        submissions: appendicesSubmissions,
        error: appendicesError,
        nextPageToken: appendicesNextPageToken,
        omit: appendicesOmitted,
      } = { omit: true },
      {
        submissions: rateCardsSubmissions,
        error: rateCardsError,
        nextPageToken: rateCardsNextPageToken,
        omit: rateCardsOmitted,
      } = { omit: true },
    ] = yield all([
      !portfoliosDone
        ? call(searchSubmissions, {
            kapp: C.KAPP_SLUG_DATASTORE,
            form: C.FORM_SLUG_PORTFOLIO,
            limit: 1000,
            search: {
              q: query.end()({
                status: 'Active',
                public: 'Public',
              }),
              include: [
                'details',
                ...C.PORTFOLIO_PRIMARY_FIELDS.map(f => `values[${f}]`),
                'form',
              ],
            },
            pageToken: portfoliosPageToken,
            public: !authenticated,
          })
        : undefined,
      !servicesDone
        ? call(searchSubmissions, {
            kapp: C.KAPP_SLUG_DATASTORE,
            form: C.FORM_SLUG_SERVICE,
            limit: 1000,
            search: {
              q: query.end()({
                status: 'Active',
                public: 'Public',
              }),
              include: [
                'details',
                ...C.SERVICE_PRIMARY_FIELDS.map(f => `values[${f}]`),
                'form',
              ],
            },
            pageToken: servicesPageToken,
            public: !authenticated,
          })
        : undefined,
      !appendicesDone
        ? call(searchSubmissions, {
            kapp: C.KAPP_SLUG_DATASTORE,
            form: C.FORM_SLUG_APPENDIX,
            limit: 1000,
            search: {
              q: query.end()({
                status: 'Active',
                public: 'Public',
              }),
              include: [
                'details',
                ...C.APPENDIX_PRIMARY_FIELDS.map(f => `values[${f}]`),
                'form',
              ],
            },
            pageToken: appendicesPageToken,
            public: !authenticated,
          })
        : undefined,
      !rateCardsDone
        ? call(searchSubmissions, {
            kapp: C.KAPP_SLUG_ADMIN,
            form: C.FORM_SLUG_RATE_CARD,
            limit: 1000,
            search: {
              q: defineKqlQuery()
                .equals(`values[${C.FIELD_STATUS}]`, 'status')
                .end()({
                status: 'Active',
              }),
              include: ['details', 'values'],
            },
            pageToken: rateCardsPageToken,
            public: !authenticated,
          })
        : undefined,
    ]);

    errors = [
      portfoliosError,
      servicesError,
      appendicesError,
      rateCardsError,
    ].filter(Boolean);

    if (!portfoliosOmitted) {
      portfolios = [...portfolios, ...(portfoliosSubmissions || [])];
      portfoliosPageToken = portfoliosNextPageToken;
      portfoliosDone = !portfoliosNextPageToken;
    }
    if (!servicesOmitted) {
      services = [...services, ...(servicesSubmissions || [])];
      servicesPageToken = servicesNextPageToken;
      servicesDone = !servicesNextPageToken;
    }
    if (!appendicesOmitted) {
      appendices = [...appendices, ...(appendicesSubmissions || [])];
      appendicesPageToken = appendicesNextPageToken;
      appendicesDone = !appendicesNextPageToken;
    }
    if (!rateCardsOmitted) {
      rateCards = [...rateCards, ...(rateCardsSubmissions || [])];
      rateCardsPageToken = rateCardsNextPageToken;
      rateCardsDone = !rateCardsNextPageToken;
    }
  } while (
    maxIterations > 0 &&
    errors.length === 0 &&
    (!portfoliosDone || !servicesDone || !appendicesDone || !rateCardsDone)
  );

  if (
    maxIterations === 0 &&
    errors.length === 0 &&
    (!portfoliosDone || !servicesDone || !appendicesDone || !rateCardsDone)
  ) {
    console.error(
      'Not all service catalog data has been fetched due to an excessive number of records. This is likely due to improper data maintenance as this data set should never be this large. Please contact an administrator.',
    );
  }

  if (errors.length > 0) {
    yield put(actions.fetchCatalogFailure(errors));
  } else {
    yield put(
      actions.fetchCatalogSuccess({
        portfolios,
        services,
        appendices,
        rateCards,
      }),
    );
  }
}

export function* watchCatalog() {
  yield takeEvery(types.FETCH_CATALOG_REQUEST, fetchCatalogRequestSaga);
}
